/**
 * AdminPoolTable Component - Phase 3H-A
 *
 * Paginated table of pools for admin management.
 * BSL Compliant: Neutral colors, factual data only.
 * Fixed: Dark theme text visibility, added delete action
 */

import { AdminPool } from '../../types';
import { Button } from '../common';
import { StatusBadge, ModeBadge } from '../common';

interface AdminPoolTableProps {
  pools: AdminPool[];
  loading?: boolean;
  onEdit?: (pool: AdminPool) => void;
  onLock?: (pool: AdminPool) => void;
  onDelete?: (pool: AdminPool) => void;
  onViewDetails?: (pool: AdminPool) => void;
  onPopulateProps?: (pool: AdminPool) => void;
  onUpdateOutcomes?: (pool: AdminPool) => void;
  populatingPool?: string | null;
  resolvingPool?: string | null;
  deletingPool?: string | null;
}

export function AdminPoolTable({
  pools,
  loading,
  onEdit,
  onLock,
  onDelete,
  onViewDetails,
  onPopulateProps,
  onUpdateOutcomes,
  populatingPool,
  resolvingPool,
  deletingPool,
}: AdminPoolTableProps): JSX.Element {
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-slate-700 rounded mb-2" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-16 bg-slate-800 rounded mb-2" />
        ))}
      </div>
    );
  }

  if (pools.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-300">No pools found</p>
      </div>
    );
  }

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '$0';
    return '$' + value.toLocaleString();
  };

  return (
    <div className="overflow-x-auto bg-slate-900 rounded-lg">
      <table className="w-full">
        <thead className="bg-slate-800">
          <tr className="border-b border-slate-600">
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-100">Pool</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-100">Domain</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-100">Mode</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-100">Status</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-slate-100">Members</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-slate-100">Bankroll</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-100">Created</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-slate-100">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-slate-900">
          {pools.map((pool) => {
            const poolKey = String(pool.pool_id);
            const isPopulating = populatingPool === poolKey;
            const isDeleting = deletingPool === poolKey;
            
            return (
              <tr
                key={pool.pool_id}
                className="border-b border-slate-700 hover:bg-slate-800 transition-colors"
              >
                <td className="py-3 px-4">
                  <div>
                    <p className="font-medium text-white">
                      {pool.name || pool.pool_id}
                    </p>
                    <p className="text-xs font-mono text-slate-400">{pool.pool_id}</p>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm uppercase text-slate-200">
                    {pool.domain || 'N/A'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <ModeBadge mode={(pool.mode || 'pick') as 'gm' | 'pick'} />
                </td>
                <td className="py-3 px-4">
                  <StatusBadge status={(pool.status || 'active') as 'pending' | 'active' | 'locked' | 'completed' | 'cancelled'} />
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="font-mono text-white">
                    {pool.member_count ?? 0}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="font-mono text-white">
                    {formatCurrency(pool.total_bankroll)}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm text-slate-300">{formatDate(pool.created_at)}</span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-2">
                    {onViewDetails && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewDetails(pool)}
                      >
                        View
                      </Button>
                    )}
                    {onPopulateProps && pool.mode === 'gm' && pool.status === 'active' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onPopulateProps(pool)}
                        disabled={isPopulating}
                      >
                        {isPopulating ? 'Populating...' : 'Populate Props'}
                      </Button>
                    )}
                    {onUpdateOutcomes && pool.mode === 'gm' && (pool.status === 'active' || pool.status === 'locked') && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onUpdateOutcomes(pool)}
                        disabled={resolvingPool === poolKey}
                      >
                        {resolvingPool === poolKey ? 'Updating...' : 'Update Outcomes'}
                      </Button>
                    )}
                    {onEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(pool)}
                      >
                        Edit
                      </Button>
                    )}
                    {onLock && pool.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onLock(pool)}
                      >
                        Lock
                      </Button>
                    )}
                    {onDelete && pool.status !== 'cancelled' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(pool)}
                        disabled={isDeleting}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
